import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { jobs, assessments, shortlist as shortlistApi, trials } from '../utils/api';
import ShortviewTalentCard from '../components/ShortviewTalentCard';
import FIFACarousel from '../components/FIFACarousel';
import TrialBookingModal from '../components/TrialBookingModal';
import { 
    Briefcase, ArrowLeft, MapPin, Clock, PoundSterling, Users,
    X, Layers, Inbox, UserCheck, Send, Sparkles, Calendar, Pencil, Trash2, Bookmark
} from 'lucide-react';
import { toast } from 'sonner';

// Trial booking statuses that place a candidate back into the "Applied" bucket
// (no active invitation yet, or the trial fell through)
const APPLIED_BOOKING_STATUSES = new Set([
    'cancelled', 'cancelled_late', 'cancelled_by_employer',
    'candidate_no_show', 'expired', 'payment_failed', 'terminated_early'
]);

// Trial booking statuses that place a candidate into the "Invited to Trial" bucket
const INVITED_BOOKING_STATUSES = new Set([
    'pending_candidate_response', 'pending_payment', 'scheduled', 'in_progress', 'completed'
]);

/**
 * RoleDetailPage - Employer view for a specific job posting
 * Shows job details and FIFA-style candidate carousel with tabbed sections
 */
const RoleDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState({
        general: [],
        applied: [],
        invited: [],
        wildcard: []
    });
    
    // Wildcard description
    const [wildcardDescription, setWildcardDescription] = useState('');
    
    // Active tab state
    const [activeTab, setActiveTab] = useState('general');
    
    // Comparison mode state
    const [compareMode, setCompareMode] = useState(false);
    const [selectedForComparison, setSelectedForComparison] = useState([]);
    
    // Expanded view state
    const [expandedCandidate, setExpandedCandidate] = useState(null);
    
    // Trial booking modal state
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedCandidateForTrial, setSelectedCandidateForTrial] = useState(null);
    
    // Edit job modal state
    const [showEditModal, setShowEditModal] = useState(false);
    
    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingJob, setDeletingJob] = useState(false);
    
    // Shortlist state
    const [shortlistedCandidates, setShortlistedCandidates] = useState([]);
    const [shortlistLoading, setShortlistLoading] = useState(false);
    
    // Alignment scores state
    const [alignmentScores, setAlignmentScores] = useState({});
    
    // Sort mode state
    const [sortMode, setSortMode] = useState('alignment');

    useEffect(() => {
        loadJobAndCandidates();
    }, [id]);

    const loadJobAndCandidates = async () => {
        try {
            // Load job details
            const jobRes = await jobs.getOne(id);
            setJob(jobRes.data);
            
            // Load wildcard description
            try {
                const descRes = await assessments.getWildcardDescription();
                setWildcardDescription(descRes.data.description);
            } catch (e) {
                setWildcardDescription("Wildcards are candidates who demonstrate exceptional potential beyond traditional role alignment.");
            }
            
            // Load matched candidates for this job
            try {
                const matchesRes = await jobs.getMatches(id);
                const matchedCandidates = matchesRes.data.matches || matchesRes.data.candidates || [];

                // Load this employer's trial bookings so we can bucket each matched
                // candidate by their booking status for this specific job.
                let bookingStatusByCandidate = {};
                try {
                    const bookingsRes = await trials.getMyBookings();
                    const jobBookings = (bookingsRes.data.bookings || []).filter(b => b.job_id === id);
                    // Bookings are returned most-recent-first; keep the latest status per candidate
                    jobBookings.forEach(b => {
                        if (!(b.candidate_id in bookingStatusByCandidate)) {
                            bookingStatusByCandidate[b.candidate_id] = b.status;
                        }
                    });
                } catch (e) {
                    console.error('Failed to load trial bookings:', e);
                }

                // Filter wildcards from real matches
                const wildcards = matchedCandidates.filter(c => c.talent_card?.wildcard?.is_wildcard);
                // Applied: no booking yet, or the trial fell through
                const applied = matchedCandidates.filter(c => {
                    const status = bookingStatusByCandidate[c.candidate_id || c.id];
                    return !status || APPLIED_BOOKING_STATUSES.has(status);
                });
                // Invited to Trial: an active/awaiting/completed booking exists
                const invited = matchedCandidates.filter(c => {
                    const status = bookingStatusByCandidate[c.candidate_id || c.id];
                    return status && INVITED_BOOKING_STATUSES.has(status);
                });

                setCandidates({
                    general: matchedCandidates,
                    applied,
                    invited,
                    wildcard: wildcards
                });

                // Fetch alignment scores for real candidates
                fetchAlignmentScores(id, matchedCandidates);
            } catch (e) {
                console.error('Failed to load matches:', e);
                setCandidates({ general: [], applied: [], invited: [], wildcard: [] });
            }
        } catch (error) {
            toast.error('Failed to load job details');
            console.error(error);
        } finally {
            setLoading(false);
        }
        
        // Load shortlist for this job
        try {
            const slRes = await shortlistApi.getAll(id);
            setShortlistedCandidates(slRes.data.shortlist || []);
        } catch (e) {
            console.error('Failed to load shortlist:', e);
        }
    };
    
    const fetchAlignmentScores = async (jobId, candidateList) => {
        const scores = {};
        for (const candidate of candidateList) {
            const candidateId = candidate.candidate_id || candidate.id;
            if (!candidateId) continue;
            try {
                const res = await jobs.getAlignment(jobId, candidateId);
                scores[candidateId] = res.data;
            } catch (e) {
                // Alignment not yet calculated or unavailable
            }
        }
        setAlignmentScores(scores);
    };
    
    // Get current tab's candidates
    const getCurrentCandidates = () => {
        if (activeTab === 'shortlist') {
            // Map shortlist entries to candidate-like objects for the carousel
            return shortlistedCandidates.map(s => ({
                id: s.candidate_id,
                name: s.candidate_name,
                shortlist_id: s.id,
                primary_role: s.candidate_snapshot?.primary_role || '',
                talent_card: {
                    header: { primary_role: s.candidate_snapshot?.primary_role || '' },
                    snapshot_score: s.candidate_snapshot?.snapshot_score || {},
                    top_skills: s.candidate_snapshot?.top_skills || [],
                    wildcard: { is_wildcard: s.candidate_snapshot?.is_wildcard || false }
                }
            }));
        }
        return candidates[activeTab] || [];
    };
    
    // Get numeric score for a candidate based on sort mode
    const getCandidateScore = (candidate, mode) => {
        const candidateId = candidate.candidate_id || candidate.id;
        if (mode === 'alignment') {
            const score = alignmentScores[candidateId]?.role_alignment_score;
            return typeof score === 'number' ? score : -Infinity;
        }
        const snapshotScore = candidate.talent_card?.snapshot_score || {};
        const overall = snapshotScore.overall_score || snapshotScore.composite_score || snapshotScore.calculated_snapshot_score || 0;
        return typeof overall === 'number' ? overall : -Infinity;
    };
    
    // Arrange sorted array into centre-outward alternating order
    // rank1→centre, rank2→1 right, rank3→1 left, rank4→2 right, rank5→2 left...
    const arrangeForCarousel = (sorted) => {
        if (sorted.length <= 1) return sorted;
        const result = new Array(sorted.length);
        result[0] = sorted[0];
        let rightPos = 1;
        let leftPos = sorted.length - 1;
        for (let i = 1; i < sorted.length; i++) {
            if (i % 2 === 1) {
                result[rightPos] = sorted[i];
                rightPos++;
            } else {
                result[leftPos] = sorted[i];
                leftPos--;
            }
        }
        return result;
    };
    
    // Get candidates sorted by active mode and arranged for carousel
    const getSortedCandidates = () => {
        const raw = getCurrentCandidates();
        if (raw.length <= 1) return raw;
        const sorted = [...raw].sort((a, b) => getCandidateScore(b, sortMode) - getCandidateScore(a, sortMode));
        return arrangeForCarousel(sorted);
    };
    
    // Tab configuration
    const tabs = [
        { 
            id: 'general', 
            label: 'General', 
            icon: Users,
            count: candidates.general.length,
            description: 'AI-matched candidates'
        },
        { 
            id: 'applied', 
            label: 'Applied', 
            icon: Inbox,
            count: candidates.applied.length,
            description: 'Candidates who applied'
        },
        { 
            id: 'invited', 
            label: 'Invited to Trial', 
            icon: Send,
            count: candidates.invited.length,
            description: 'Awaiting trial'
        },
        { 
            id: 'wildcard', 
            label: 'Wildcard', 
            icon: Sparkles,
            count: candidates.wildcard.length,
            description: 'Exceptional cross-domain potential',
            isSpecial: true
        }
    ];
    
    // Comparison handlers
    const handleCompare = (candidate) => {
        setSelectedForComparison(prev => {
            const isSelected = prev.some(c => c.id === candidate.id);
            if (isSelected) {
                return prev.filter(c => c.id !== candidate.id);
            }
            if (prev.length >= 5) {
                toast.error('Maximum 5 candidates for comparison');
                return prev;
            }
            return [...prev, candidate];
        });
    };
    
    const startComparison = () => {
        if (selectedForComparison.length < 2) {
            toast.error('Select at least 2 candidates to compare');
            return;
        }
        setCompareMode(true);
    };
    
    // Candidate card actions
    const handleExpand = (candidate) => {
        setExpandedCandidate(candidate);
    };
    
    const handleShortlist = async (candidate) => {
        const alreadyShortlisted = shortlistedCandidates.find(s => s.candidate_id === candidate.id);
        if (alreadyShortlisted) {
            try {
                await shortlistApi.remove(alreadyShortlisted.id);
                setShortlistedCandidates(prev => prev.filter(s => s.id !== alreadyShortlisted.id));
                toast.success(`${candidate.name?.split(' ')[0] || 'Candidate'} removed from shortlist`);
            } catch (error) {
                toast.error('Failed to remove from shortlist');
            }
        } else {
            try {
                const res = await shortlistApi.add({
                    job_id: id,
                    candidate_id: candidate.id,
                    candidate_name: candidate.name || '',
                    candidate_snapshot: {
                        name: candidate.name,
                        primary_role: candidate.talent_card?.header?.primary_role || candidate.primary_role,
                        snapshot_score: candidate.talent_card?.snapshot_score || candidate.snapshot_score,
                        top_skills: candidate.talent_card?.top_skills || [],
                        is_wildcard: candidate.talent_card?.wildcard?.is_wildcard || false
                    }
                });
                setShortlistedCandidates(prev => [...prev, res.data]);
                toast.success(`${candidate.name?.split(' ')[0] || 'Candidate'} added to shortlist`);
            } catch (error) {
                if (error.response?.status === 400) {
                    toast.info('Candidate already shortlisted');
                } else {
                    toast.error('Failed to add to shortlist');
                }
            }
        }
    };
    
    const isCandidateShortlisted = (candidateId) => {
        return shortlistedCandidates.some(s => s.candidate_id === candidateId);
    };
    
    const handleDeleteJob = async () => {
        setDeletingJob(true);
        try {
            await jobs.delete(id);
            toast.success('Job deleted successfully');
            navigate('/company/dashboard');
        } catch (error) {
            toast.error(error.userMessage || 'Failed to delete job');
        } finally {
            setDeletingJob(false);
            setShowDeleteConfirm(false);
        }
    };
    
    const handleBookTrialClick = (candidate) => {
        setSelectedCandidateForTrial(candidate);
        setShowBookingModal(true);
    };
    
    const handleCloseBookingModal = () => {
        setShowBookingModal(false);
        setSelectedCandidateForTrial(null);
    };
    
    // Render card for carousel
    const renderCarouselCard = (candidate, isCenter) => {
        const candidateId = candidate.candidate_id || candidate.id;
        return (
            <ShortviewTalentCard
                candidate={candidate}
                onExpand={handleExpand}
                onShortlist={handleShortlist}
                onCompare={handleCompare}
                onBookTrial={handleBookTrialClick}
                isSelected={selectedForComparison.some(c => c.id === candidate.id)}
                isWildcard={candidate.talent_card?.wildcard?.is_wildcard || activeTab === 'wildcard'}
                alignmentScore={alignmentScores[candidateId] || null}
            />
        );
    };
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#8B5CF6] border-t-transparent mx-auto mb-4"></div>
                    <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>Loading candidates...</p>
                </div>
            </div>
        );
    }
    
    if (!job) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Job not found</h2>
                    <Button onClick={() => navigate('/company/dashboard')} className="btn-primary">Back to Dashboard</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            {/* Header */}
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/company/dashboard')}
                            className="p-2 text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center space-x-2">
                            <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</h1>
                        </div>
                    </div>
                    
                    {/* Comparison indicator */}
                    {selectedForComparison.length > 0 && !compareMode && (
                        <div className="flex items-center space-x-3 bg-purple-50 px-4 py-2 rounded-full border border-purple-200/50">
                            <span className="text-sm font-semibold text-[#8B5CF6]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                {selectedForComparison.length} selected
                            </span>
                            <Button 
                                onClick={startComparison}
                                size="sm"
                                className="btn-primary"
                            >
                                <Layers className="h-4 w-4 mr-2" />
                                Compare
                            </Button>
                            <Button 
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedForComparison([])}
                                className="text-[#8B5CF6] hover:bg-purple-100"
                            >
                                Clear
                            </Button>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Job Summary Card */}
                <div className="bg-white rounded-3xl p-6 border border-purple-200/50 shadow-sm mb-8">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Current Role</span>
                            <h2 className="text-3xl font-bold mb-2 tracking-tight" style={{ color: '#0A0A0A', fontFamily: 'Unbounded, sans-serif' }}>
                                {job.title}
                            </h2>
                            <p className="text-slate-500 mb-4 max-w-2xl" style={{ fontFamily: 'Barlow, sans-serif' }}>{job.description}</p>
                            
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                                <div className="flex items-center space-x-2">
                                    <MapPin className="h-4 w-4 text-[#8B5CF6]" />
                                    <span className="text-slate-600">{job.location}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Clock className="h-4 w-4 text-[#8B5CF6]" />
                                    <span className="text-slate-600">{job.trial_length_days} day trial</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <PoundSterling className="h-4 w-4 text-[#10B981]" />
                                    <span className="font-semibold text-[#10B981]" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{job.pay_rate || 13}/hr</span>
                                </div>
                                {job.salary && (
                                    <div className="flex items-center space-x-2">
                                        <Briefcase className="h-4 w-4 text-[#10B981]" />
                                        <span className="font-semibold text-[#059669]">{job.salary}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowEditModal(true)}
                                className="rounded-full border-purple-200 text-[#8B5CF6] hover:bg-purple-50 hover:border-purple-400"
                                data-testid="edit-job-btn"
                            >
                                <Pencil className="h-4 w-4 mr-1.5" />
                                Edit Job
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="rounded-full border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400"
                                data-testid="delete-posting-btn"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" />
                                Delete Posting
                            </Button>
                            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                                job.status === 'active' 
                                    ? 'bg-[#ECFDF5] text-[#059669]' 
                                    : 'bg-slate-100 text-slate-500'
                            }`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                {job.status}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* AI Matched Candidates Section */}
                <div className="mb-6">
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Talent Pool
                    </span>
                    <h2 className="text-2xl font-bold mb-2 tracking-tight" style={{ color: '#0A0A0A', fontFamily: 'Unbounded, sans-serif' }}>
                        AI Matched Candidates
                    </h2>
                    <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Browse candidates matched to this role based on their Talent Card assessment
                    </p>
                    
                    {/* Horizontal Tabs */}
                    <div className="flex items-center mb-8 bg-white p-2 rounded-2xl border border-purple-200/50 shadow-sm">
                        <div className="flex items-center space-x-2 flex-1">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            const isWildcardTab = tab.id === 'wildcard';
                            
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        flex items-center space-x-2 px-5 py-3 rounded-lg font-medium transition-all duration-200
                                        ${isActive && !isWildcardTab
                                            ? 'bg-purple-600 text-white shadow-md' 
                                            : isActive && isWildcardTab
                                            ? 'text-slate-700 shadow-lg'
                                            : isWildcardTab
                                            ? 'text-slate-500 hover:bg-slate-50 font-semibold'
                                            : 'text-gray-600 hover:bg-gray-100'
                                        }
                                    `}
                                    style={isActive && isWildcardTab ? {
                                        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 50%, #94a3b8 100%)',
                                        boxShadow: '0 4px 15px rgba(148, 163, 184, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
                                    } : {}}
                                    data-testid={`tab-${tab.id}`}
                                >
                                    <Icon className={`h-5 w-5 ${
                                        isActive && isWildcardTab ? 'text-slate-600' :
                                        isActive ? 'text-white' : 
                                        isWildcardTab ? 'text-slate-400' : 'text-gray-400'
                                    }`} />
                                    <span style={isWildcardTab ? { fontFamily: 'Unbounded, sans-serif' } : {}}>
                                        {tab.label}
                                    </span>
                                </button>
                            );
                        })}
                        </div>
                        <button
                            onClick={() => setActiveTab('shortlist')}
                            className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-medium transition-all duration-200 ml-auto border-l border-purple-100 pl-5 ${
                                activeTab === 'shortlist' ? 'bg-purple-600 text-white shadow-md' : 'text-[#8B5CF6] hover:bg-purple-50'
                            }`}
                            data-testid="tab-shortlist"
                        >
                            <Bookmark className="h-5 w-5" fill={activeTab === 'shortlist' ? '#fff' : '#8B5CF6'} />
                            <span style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '0.875rem' }}>Shortlist ({shortlistedCandidates.length})</span>
                        </button>
                    </div>
                    
                    {/* Wildcard Description - Only shown when Wildcard tab is active */}
                    {activeTab === 'wildcard' && (
                        <div 
                            className="mb-6 p-4 rounded-xl border-2 border-slate-300"
                            style={{
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)'
                            }}
                        >
                            <div className="flex items-start space-x-3">
                                <div 
                                    className="p-2 rounded-lg"
                                    style={{
                                        background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)'
                                    }}
                                >
                                    <Sparkles className="h-6 w-6 text-slate-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-700 mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                        What are Wildcards?
                                    </h4>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {wildcardDescription || "Wildcards are candidates who demonstrate exceptional potential beyond traditional role alignment. They show strong cross-domain adaptability, rapid learning capability, and innovative thinking patterns that suggest they may excel in ways a CV alone wouldn't predict."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Sort Toggle + FIFA-Style Carousel */}
                <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-3xl p-4 mb-8 border border-gray-300">
                    {/* Sort Mode Toggle */}
                    <div className="flex items-center justify-between mb-4 px-2">
                        <div className="flex items-center bg-white rounded-full p-1 border border-purple-200/50 shadow-sm" data-testid="sort-toggle">
                            <button
                                onClick={() => setSortMode('alignment')}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                                    sortMode === 'alignment'
                                        ? 'text-white shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                style={sortMode === 'alignment' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                data-testid="sort-alignment-btn"
                            >
                                Role Alignment
                            </button>
                            <button
                                onClick={() => setSortMode('snapshot')}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                                    sortMode === 'snapshot'
                                        ? 'text-white shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                }`}
                                style={sortMode === 'snapshot' ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                data-testid="sort-snapshot-btn"
                            >
                                Snapshot Score
                            </button>
                        </div>
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="sort-label">
                            {sortMode === 'alignment'
                                ? 'Ranked by fit for this role.'
                                : 'Ranked by overall candidate quality.'
                            }
                        </p>
                    </div>

                    <FIFACarousel
                        key={`${activeTab}-${sortMode}`}
                        cards={getSortedCandidates()}
                        renderCard={renderCarouselCard}
                        onCardSelect={handleExpand}
                        emptyMessage={`No ${activeTab} candidates yet`}
                    />
                </div>
            </main>
            
            {/* Comparison Mode Overlay */}
            {compareMode && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-teal-50">
                            <h3 className="text-xl font-bold" style={{ color: '#92400E' }}>
                                Comparing {selectedForComparison.length} Candidates
                            </h3>
                            <Button variant="ghost" onClick={() => setCompareMode(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="p-6 overflow-x-auto bg-gray-50">
                            <div className="flex space-x-5 justify-center">
                                {selectedForComparison.map(candidate => (
                                    <ShortviewTalentCard
                                        key={candidate.id}
                                        candidate={candidate}
                                        onExpand={handleExpand}
                                        onShortlist={handleShortlist}
                                        isSelected={true}
                                        alignmentScore={alignmentScores[candidate.candidate_id || candidate.id] || null}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Expanded View Modal */}
            {expandedCandidate && (
                <ExpandedCandidateView 
                    candidate={expandedCandidate} 
                    onClose={() => setExpandedCandidate(null)}
                    onShortlist={handleShortlist}
                    onBookTrial={handleBookTrialClick}
                />
            )}
            
            {/* Trial Booking Modal */}
            <TrialBookingModal
                isOpen={showBookingModal}
                onClose={handleCloseBookingModal}
                candidate={selectedCandidateForTrial}
                jobId={id}
                jobTitle={job?.title}
            />
            
            {/* Edit Job Modal */}
            {showEditModal && (
                <EditJobModal 
                    job={job} 
                    onClose={() => setShowEditModal(false)}
                    onSave={(updatedJob) => {
                        setJob(updatedJob);
                        setShowEditModal(false);
                        toast.success('Job updated successfully');
                    }}
                />
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" data-testid="delete-confirm-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Delete Job Posting</h3>
                        </div>
                        <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Are you sure you want to delete <strong>{job.title}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-full">Cancel</Button>
                            <Button
                                onClick={handleDeleteJob}
                                disabled={deletingJob}
                                className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                                data-testid="confirm-delete-btn"
                            >
                                {deletingJob ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * EditJobModal - Inline edit for job details
 */
const EditJobModal = ({ job, onClose, onSave }) => {
    const [form, setForm] = useState({
        title: job.title || '',
        description: job.description || '',
        location: job.location || '',
        pay_rate: job.pay_rate || '',
        salary: job.salary || '',
        trial_length_days: job.trial_length_days || 5,
        skills: (job.skills || []).join(', '),
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const updates = {
                title: form.title,
                description: form.description,
                location: form.location,
                pay_rate: parseFloat(form.pay_rate),
                salary: form.salary || null,
                trial_length_days: parseInt(form.trial_length_days),
                skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
            };
            const res = await jobs.update(job.id, updates);
            onSave(res.data);
        } catch (err) {
            toast.error(err.userMessage || 'Failed to update job');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
                <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: '#1E3A5F', fontFamily: 'Unbounded, sans-serif' }}>
                        <Pencil className="inline h-4 w-4 mr-2" />Edit Job
                    </h3>
                    <Button variant="ghost" onClick={onClose} className="p-1"><X className="h-5 w-5" /></Button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                        <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="edit-job-title" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4} data-testid="edit-job-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                            <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} data-testid="edit-job-location" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Pay Rate (£/hr)</label>
                            <Input type="number" step="0.01" min="11.44" value={form.pay_rate} onChange={(e) => setForm({...form, pay_rate: e.target.value})} data-testid="edit-job-payrate" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Salary</label>
                            <Input value={form.salary} onChange={(e) => setForm({...form, salary: e.target.value})} placeholder="e.g. £30,000 - £40,000" data-testid="edit-job-salary" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Length (days)</label>
                            <Input type="number" min="1" max="10" value={form.trial_length_days} onChange={(e) => setForm({...form, trial_length_days: e.target.value})} data-testid="edit-job-trialdays" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Skills (comma-separated)</label>
                        <Input value={form.skills} onChange={(e) => setForm({...form, skills: e.target.value})} placeholder="e.g. Excel, Communication, Leadership" data-testid="edit-job-skills" />
                    </div>
                </div>
                <div className="p-5 border-t border-gray-200 flex justify-end space-x-3">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-job-btn">
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

/**
 * ExpandedCandidateView - Detailed view using AI-generated talent card sections
 */
const ExpandedCandidateView = ({ candidate, onClose, onShortlist, onBookTrial }) => {
    const [showEvidence, setShowEvidence] = useState(false);
    const { talent_card, name } = candidate;

    // Pull real sections from talent_card; render only what AI has produced
    const microtest = talent_card?.microtest || {};
    const problemSolving = talent_card?.problem_solving || {};
    const workingStyle = talent_card?.working_style || {};
    const achievements = talent_card?.achievements || {};

    const sections = [
        {
            number: '1',
            title: 'Microtest Results',
            summary: [
                microtest.expanded_view?.thinking_approach,
                microtest.expanded_view?.execution_clarity,
                microtest.expanded_view?.learning_signals
            ].filter(Boolean).join(' '),
            highlights: microtest.short_view?.observed_strengths || []
        },
        {
            number: '2',
            title: 'Problem Solving & Thinking Style',
            summary: [
                problemSolving.expanded_view?.ambiguity_handling,
                problemSolving.expanded_view?.decision_making,
                problemSolving.expanded_view?.learning_approach
            ].filter(Boolean).join(' '),
            highlights: problemSolving.short_view?.thinking_tags || []
        },
        {
            number: '3',
            title: 'Working Style & Preferences',
            summary: [
                workingStyle.expanded_view?.workday_structure,
                workingStyle.expanded_view?.manager_style,
                workingStyle.expanded_view?.team_fit,
                workingStyle.expanded_view?.next_role_intent
            ].filter(Boolean).join(' '),
            highlights: [
                workingStyle.short_view?.feedback_style,
                workingStyle.short_view?.environment_fit,
                workingStyle.short_view?.motivation_summary
            ].filter(Boolean)
        },
        {
            number: '4',
            title: 'Achievements & Proof',
            summary: achievements.expanded_view?.credibility_summary || '',
            highlights: achievements.short_view?.headline_achievements || []
        }
    ];

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-teal-50">
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: '#1E3A5F', fontFamily: 'Unbounded, sans-serif' }}>
                            {(name || 'Candidate').split(' ')[0]}
                        </h2>
                        <p className="text-gray-600">{talent_card?.header?.primary_role || 'Professional'}</p>
                    </div>
                    <Button variant="ghost" onClick={onClose} className="p-2 hover:bg-white/50">
                        <X className="h-6 w-6" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Assessment-only notice — shown when candidate hasn't yet completed a trial shift */}
                {!talent_card?.trial_feedback && (
                    <div
                        className="rounded-xl px-5 py-4 border"
                        style={{ backgroundColor: '#F5F3FF', borderColor: '#DDD6FE' }}
                        data-testid="assessment-only-banner"
                    >
                        <p className="text-sm leading-snug" style={{ color: '#5B21B6', fontFamily: 'Barlow, sans-serif' }}>
                            <strong>Scores shown are based on assessment data.</strong>{' '}
                            <strong>All scores are strengthened with real-world signal after a trial shift.</strong>
                        </p>
                    </div>
                )}

                {sections.map((section) => (
                        <div key={section.number} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                            <h3 className="text-lg font-bold mb-3" style={{ color: '#92400E' }}>
                                {section.number}. {section.title}
                            </h3>
                            {section.summary ? (
                                <p className="text-gray-700 mb-4 leading-relaxed">{section.summary}</p>
                            ) : (
                                <p className="text-slate-400 italic mb-4 leading-relaxed">Detailed AI analysis pending for this section.</p>
                            )}
                            {section.highlights.length > 0 && (
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <p className="text-sm font-semibold text-gray-500 mb-2">Observed Pattern Highlights:</p>
                                    <ul className="space-y-1">
                                        {section.highlights.map((h, i) => (
                                            <li key={i} className="text-sm text-gray-600 flex items-start">
                                                <span className="mr-2 text-purple-500">•</span>{h}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                    <Button 
                        variant="outline"
                        onClick={() => setShowEvidence(true)}
                        className="border-gray-300"
                    >
                        View Candidate Responses
                    </Button>
                    <div className="flex items-center space-x-3">
                        <Button 
                            variant="outline"
                            className="border-purple-300 text-purple-700"
                            onClick={() => { onShortlist(candidate); }}
                        >
                            Shortlist
                        </Button>
                        <Button 
                            className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90 px-6"
                            onClick={() => { onBookTrial?.(candidate); onClose(); }}
                            data-testid="expanded-book-trial-btn"
                        >
                            <Calendar className="mr-2 h-4 w-4" />
                            Book Trial
                        </Button>
                    </div>
                </div>
            </div>

            {/* Evidence Modal */}
            {showEvidence && (
                <EvidenceView 
                    candidate={candidate}
                    onClose={() => setShowEvidence(false)}
                />
            )}
        </div>
    );
};

/**
 * EvidenceView - Real assessment responses from the candidate
 */
const EvidenceView = ({ candidate, onClose }) => {
    const responses = candidate.assessment_responses || candidate.talent_card?.assessment_responses || [];

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                    <h3 className="text-lg font-bold" style={{ color: '#1E3A5F' }}>
                        Candidate Responses
                    </h3>
                    <Button variant="ghost" onClick={onClose} className="p-1">
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {responses.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                Detailed candidate responses are not available for this profile.
                            </p>
                        </div>
                    ) : (
                        responses.map((item, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded">Q{i + 1}</span>
                                    {item.time && (
                                        <span className="text-xs text-gray-400 flex items-center">
                                            <Clock className="h-3 w-3 mr-1" />
                                            {item.time}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm font-medium text-gray-800 mb-3">{item.question}</p>
                                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-100 leading-relaxed">{item.answer}</p>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoleDetailPage;
